import React, { useCallback, useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import '../public/static/assets/app.css';
import 'suneditor/dist/css/suneditor.min.css';
import '../app/styles/admin.css';
import * as Sentry from '@sentry/nextjs';
import { ToastProvider } from '../app/providers/ToastContext';
import { ModalProvider } from '../app/providers/ModalContext';
import Toast from '../app/components/Toast';
import Modal from '../app/components/Modal';
import AppNavigation from '../app/components/AppNavigation';
import { SheetProvider } from '../app/providers/SheetContext';
import Sheet from '../app/components/Sheet';
import { PlanProvider } from '../app/providers/UpgradePlanContext';
import { SWRConfig } from 'swr';

import * as gtag from '../app/utils/gtag';
import { resolveShopDomain, impersonationShop } from '../app/lib/shop-domain';
// const ENTRY_PAGES = ['/shopify/callback', '/auth/impersonate'];

// @ts-ignore
const MyApp = ({ Component, pageProps }) => {
  const router = useRouter();
  const [childLoading, setLoading] = useState(false);
  const handleSetLoading = useCallback((bool: boolean) => {
    setSubmit(bool);
    setLoading(bool);
  }, []);
  const [submit, setSubmit] = useState(false);
  const [discard, setDiscard] = useState(false);
  const [primaryDisable] = useState(false);
  const [saveEnabled, setEnabled] = useState(false);
  const [hideLayout] = useState(false);
  const [, setIsSearchActive] = useState(false);
  const [, setSearchValue] = useState('');

  // Save-bar signals are one-shot pulses, not sticky state. Every editor page
  // consumes `onSubmit`/`onDiscard` from an effect whose deps include the whole
  // `props` object, so that effect re-runs on every render of this component --
  // a flag left latched at `true` would re-fire the page's save/discard handler
  // on each of those renders. Child effects flush before the parent's, so the
  // page has already acted on the pulse by the time this clears it: one Save
  // click triggers exactly one save.
  useEffect(() => {
    if (submit) setSubmit(false);
    if (discard) setDiscard(false);
  }, [submit, discard]);

  useEffect(() => {
    const handleRouteChange = (url: URL) => {
      gtag.pageview(url);
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.pathname, router.events, hideLayout]);

  // Handler for Polaris web components navigation events
  useEffect(() => {
    const handleShopifyNavigate = (event: CustomEvent) => {
      const href = (event.target as HTMLElement)?.getAttribute('href');
      if (href) {
        router.push(href);
      }
    };

    document.addEventListener('shopify:navigate', handleShopifyNavigate as EventListener);
    return () => {
      document.removeEventListener('shopify:navigate', handleShopifyNavigate as EventListener);
    };
  }, [router]);

  const handleSetEnabled = useCallback((bool: boolean) => {
    setEnabled(bool);
    if (discard && bool) setDiscard(false);
    if (submit && bool) setSubmit(false);
  }, [discard, submit]);
  useCallback((value) => {
    setSearchValue(value);
    setIsSearchActive(value.length > 0);
  }, []);

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // Attach the shop identity to Sentry so client errors are attributable.
  useEffect(() => {
    const shop = resolveShopDomain();
    if (!shop) return;
    Sentry.setUser({ id: shop, username: shop });
    Sentry.setTag('shop.domain', shop);
  }, []);

  // Admin ops console: a fully separate surface. Render admin pages bare —
  // no Polaris/App Bridge providers, no save bar, no iframe gating. Merchant
  // routes below are untouched.
  if (router.pathname.startsWith('/admin')) {
    return <Component {...pageProps} />;
  }

  if (!mounted) {
    return (
      <React.Fragment>
        <Head>
          <title>Tuneboom - Shopify Music Distribution</title>
          <link rel='shortcut icon' href='/static/favicon.png' />
          <meta charSet='utf-8' />
        </Head>
      </React.Fragment>
    );
  }

  // Renders into the native admin sidebar via App Bridge (nothing in-page).
  const navigation = <AppNavigation />;
  const isInFrame = window?.top !== window?.self;
  // Impersonation (admin support session) runs standalone outside the iframe,
  // authenticated by the headless stub in _document. Dev likewise renders
  // standalone for headless testing. Production merchants still require embedding.
  const impersonatingShop = impersonationShop();
  const showComponent =
    (isInFrame || process.env.APP_ENV === 'development' || Boolean(impersonatingShop)) && (
    // Focus/reconnect revalidation off globally: closing an App Bridge modal
    // (e.g. the upload modal) refocuses the window and would otherwise refetch
    // every mounted hook at once, flashing the whole page. Individual pages
    // used to opt out one-by-one; this makes it the app-wide default.
    <SWRConfig value={{ revalidateOnFocus: false, revalidateOnReconnect: false }}>
      <ToastProvider>
        <ModalProvider>
          <SheetProvider>
            <PlanProvider>
              <div style={{ minHeight: '100vh' }}>
                {impersonatingShop && <ImpersonationBanner shop={impersonatingShop} />}
                {!hideLayout && navigation}
                <RenderSaveBar
                  enabled={saveEnabled}
                  loading={childLoading}
                  setDiscard={setDiscard}
                  setSubmit={setSubmit}
                  setLoading={setLoading}
                  primaryDisable={primaryDisable}
                />
                <Toast />
                <Modal />
                <Sheet />
                <Component
                  {...pageProps}
                  barEnabled={handleSetEnabled}
                  setLoading={handleSetLoading}
                  onSubmit={submit}
                  onDiscard={discard}
                  primaryDisable={primaryDisable}
                />
              </div>
            </PlanProvider>
          </SheetProvider>
        </ModalProvider>
      </ToastProvider>
    </SWRConfig>
  );

  return (
    <React.Fragment>
      <Head>
        <title>Tuneboom - Shopify Music Distribution</title>
        <link rel='shortcut icon' href='/static/favicon.png' />
        <meta charSet='utf-8' />
      </Head>
      {showComponent}
    </React.Fragment>
  );
};

function RenderSaveBar({ setSubmit, setLoading, primaryDisable, setDiscard, loading, enabled }) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!inputRef.current) return;
    // Set value different from defaultValue to show save bar, same to hide
    inputRef.current.value = enabled ? 'dirty' : '';
    inputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
  }, [enabled]);

  return (
    <form
      data-save-bar
      onSubmit={(e) => {
        e.preventDefault();
        setSubmit(true);
        setLoading(true);
      }}
      onReset={(e) => {
        e.preventDefault();
        setDiscard(true);
      }}
      style={{ display: 'none' }}
    >
      <input ref={inputRef} name='_save_state' defaultValue='' />
    </form>
  );
}

// Persistent banner shown while an admin impersonation session is active, so a
// support operator always knows they are acting AS a merchant and can end it.
// "Exit" clears the session cookie server-side, then closes the tab (falling
// back to a blank page — a reload without the cookie would just bounce to
// Shopify via App Bridge).
function ImpersonationBanner({ shop }: { shop: string }) {
  const [exiting, setExiting] = useState(false);
  const exit = async () => {
    setExiting(true);
    try {
      await fetch('/api/auth/impersonate/stop', { method: 'POST' });
    } catch {
      // Best-effort: still tear the tab down below.
    }
    window.close();
    window.location.replace('about:blank');
  };
  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 2147483647,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: '8px 16px',
        background: '#b98900',
        color: '#fff',
        fontSize: 13,
        fontWeight: 600,
      }}
    >
      <span>Impersonating {shop} — admin session</span>
      <button
        type='button'
        onClick={exit}
        disabled={exiting}
        style={{
          border: '1px solid rgba(255,255,255,0.6)',
          background: 'transparent',
          color: '#fff',
          borderRadius: 4,
          padding: '2px 10px',
          cursor: 'pointer',
          fontWeight: 600,
        }}
      >
        {exiting ? 'Exiting…' : 'Exit'}
      </button>
    </div>
  );
}

type IdTokenProvider = { idToken?: () => Promise<string> };

// DEV-ONLY headless auth: when the app runs in development OUTSIDE the Shopify
// iframe (standalone browser / no App Bridge session), fetch a minted session
// token from the dev-only server endpoint instead of App Bridge's idToken(). In
// any non-development build this whole branch is dead (APP_ENV inlined) and the
// endpoint 404s, so production behavior is unchanged.
let devTokenCache: { token: string; exp: number } | null = null;
async function devSessionToken(): Promise<string | undefined> {
  if (process.env.APP_ENV !== 'development') return undefined;
  if (typeof window === 'undefined' || window.top !== window.self) return undefined;
  const now = Date.now();
  if (devTokenCache && devTokenCache.exp > now + 10000) return devTokenCache.token;
  try {
    const res = await fetch(`${process.env.APP_URL}/api/dev/session-token`);
    if (!res.ok) return undefined;
    const data = await res.json();
    devTokenCache = { token: data.token, exp: now + (data.expiresIn ?? 240) * 1000 };
    return data.token;
  } catch {
    return undefined;
  }
}

export function userLoggedInFetch(
  app: IdTokenProvider,
): (uri: RequestInfo, options?: RequestInit) => Promise<Response> {
  return async (uri, options = {}) => {
    let token = await devSessionToken();
    if (!token) {
      token = app && typeof app.idToken === 'function' ? await app.idToken() : undefined;
    }
    // Pass the shop the client already knows so backend logs attribute every
    // admin API call to a merchant (`x-tuneboom-shop`; read by the server's
    // ShopLogContextInterceptor). Log-attribution only — never authorization.
    const shop = resolveShopDomain();
    const headers = {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(shop ? { 'x-tuneboom-shop': shop } : {}),
    };
    return fetch(uri, { ...options, headers });
  };
}

// @ts-ignore
MyApp.getInitialProps = async ({ ctx }) => {
  return {
    shop: ctx.query.shop,
  };
};
export default MyApp;
